import { useMemo } from 'react'
import useSWR from 'swr'
import { useTranslation } from 'react-i18next'

import { assistantTurnsApi } from '@/lib/api'
import { cn } from '@/lib/utils'

import type { AssistantThreadCredits } from '@digital-twins/shared-types'

type ReplyCredits = AssistantThreadCredits['replies'][number]

export interface UseAssistantThreadCreditsResult {
  /** Per reply, keyed by the assistant message token. */
  byMessageToken: ReadonlyMap<string, ReplyCredits>
}

/**
 * Per-reply costs of an Assistant conversation (credit metering Step 8 API). Reads only for an enrolled (`on`) account: shadow and off fetch nothing and show
 * nothing. `refreshKey` revalidates after a reply; pending settlements are polled until they
 * settle. Amounts are millicredits.
 */
export function useAssistantThreadCredits(
  threadToken: string | null | undefined,
  isEnrolled: boolean,
  refreshKey: string | number,
): UseAssistantThreadCreditsResult {
  const { data } = useSWR<AssistantThreadCredits>(
    isEnrolled && threadToken ? ['assistant-thread-credits', threadToken, refreshKey] : null,
    () => assistantTurnsApi.credits(threadToken as string),
    {
      revalidateOnFocus: false,
      keepPreviousData: true,
      refreshInterval: (latest) => (latest?.replies.some((reply) => reply.settlement === 'pending') ? 4000 : 0),
    },
  )
  return useMemo(() => {
    const byMessageToken = new Map<string, ReplyCredits>()
    for (const reply of data?.replies ?? []) {
      if (reply.assistantMessageToken) {
        byMessageToken.set(reply.assistantMessageToken, reply)
      }
    }
    return { byMessageToken }
  }, [data])
}

export interface AssistantReplyCostProps {
  credits: ReplyCredits | undefined
  className?: string
}

/**
 * The note under a reply that stopped at its credit limit. A reply never shows what it cost: that
 * lives in the credit dropdown and the usage log.
 */
export function AssistantReplyCost(props: AssistantReplyCostProps) {
  const { credits, className } = props
  const { t } = useTranslation('app')

  if (!credits?.limitReached) {
    return null
  }

  return (
    <div
      className={cn('flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground', className)}
      data-testid="assistant-reply-cost"
    >
      <span data-testid="assistant-reply-limit-note">{t('threads.detail.replyLimitNote')}</span>
    </div>
  )
}
